"use client";

import { useState, useEffect } from "react";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRole,
  createRole,
  updateRole,
  deleteRole,
} from "@/store/modules/roles/thunks";
import { fetchPermissions } from "@/store/modules/permissions/thunks";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { roleSchema } from "@/validations";
import { useAuth } from "@/context/AuthContext";

const useRole = (onSuccess?: () => void, modelType: string = "") => {
  const { params, setParams, handlePage } = useParams();
  const { establishment, baseURL } = useAuth();
  const dispatch: AppDispatch = useDispatch();
  const [roleSlug, setroleSlug] = useState<string | null>(null);
  const [establishment_id, setEstablishment_id] = useState<number | null>(null);

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
    }));
  }, [modelType, setParams]);

  useEffect(() => {
    // Aguarda establishment e baseURL estarem prontos antes de fazer requests
    if (!baseURL || !establishment) return;

    dispatch(fetchRole({ without_pagination: 0 }));
  }, [dispatch, baseURL, establishment]);

  useEffect(() => {
    // Aguarda establishment e baseURL estarem prontos antes de fazer requests
    if (!baseURL || !establishment) return;

    dispatch(fetchPermissions({}));
  }, [dispatch, baseURL, establishment]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(roleSchema),
    defaultValues: {
      name: "",
      description: "",
      permissions: [],
    },
  });

  const setFormData = (data: Partial<typeof roleSchema.__outputType>) => {
    reset(data);
  };

  const { permissions, loading: permissionsLoading } = useSelector(
    (state: any) => state.premissions,
  );

  const onSubmit = async (data: typeof roleSchema.__outputType) => {
    try {
      let result;

      if (roleSlug) {
        result = await dispatch(updateRole({ slug: roleSlug, ...data }));
      } else {
        result = await dispatch(createRole(data));
      }

      if (
        createRole.fulfilled.match(result) ||
        updateRole.fulfilled.match(result)
      ) {
        dispatch(fetchRole(params));
        if (onSuccess) onSuccess();
        reset();
        setroleSlug(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados da função:", error);
    }
  };

  const { roles, loading, error } = useSelector((state: any) => state.roles);

  const totalItems = roles?.total ?? 0;
  const itemsPerPage = roles?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  const [currentPage, setCurrentPage] = useState(roles?.current_page ?? 1);

  useEffect(() => {
    if (roles?.current_page && roles.current_page !== currentPage) {
      setCurrentPage(roles.current_page);
    }
  }, [roles?.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleDelete = async () => {
    console.log("Deleting role with slug:", roleSlug);
    try {
      if (roleSlug) {
        await dispatch(deleteRole(roleSlug));
        setroleSlug(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao deletar função:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    roles,
    loading,
    error,
    setroleSlug,
    handleDelete,
    setFormData,
    params,
    setParams,
    handlePage,
    permissions,
    permissionsLoading,
    setValue,
    getValues,
    setEstablishment_id,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
  };
};

export default useRole;
